import React, { useState } from 'react';
import { SubmitHandler, useForm } from "react-hook-form";
import useThemeStore from "../store/themeStore";
import useTokenStore from '../store/store';
import { ContactFormProps } from "../utils/properties";
import { ThemeProps } from "../utils/theme";
import backgroundPattern from "../images/vesta/vesta-property-pattern-01-teal-bg.svg";
import image1 from "../images/vesta/large-cityscape-removebg.png"; // Importing the image
import { baseUrl } from '../utils';

const GenInTouch = () => {
  const getThemes = useThemeStore(
    (state) => state.theme as unknown as ThemeProps
  );
  
  // Add this line to get token from Zustand store
  const getToken = useTokenStore((state) => state.token);

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
  } = useForm<ContactFormProps>();

  const onSubmit: SubmitHandler<ContactFormProps> = async (data: ContactFormProps) => {
    setLoading(true);
    try {
      // Prepare request data
      const requestData = {
        ...data,
        website: getThemes?.name || window.location.hostname || 'Unknown Website',
        sourceUrl: window.location.href // Add current page URL
      };

      console.log('🚀 Submitting form data:', requestData);

      // Prepare headers
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Add auth token if available (same logic as Property.tsx)
      if (getToken && typeof getToken === 'string') {
        const cleanToken = (getToken as string).replace("B9I9N9456", "");
        headers['x-auth-token'] = cleanToken;
        headers['Authorization'] = `Bearer ${cleanToken}`;
        console.log('🔐 Including auth token for authenticated user');
      } else {
        console.log('👤 Submitting as anonymous user');
      }

      const response = await fetch(`${baseUrl}/whitelabel/getintouch`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Server error:', errorData);
        throw new Error(errorData.message || errorData.msg || 'Failed to submit form');
      }

      const result = await response.json();
      console.log('✅ Form submitted successfully:', result);

      // Reset form fields
      setFirstname("");
      setLastname("");
      setEmail("");
      setPhone("");
      setDescription("");
      
      // Set success message
      setMessage("Thank you for getting in touch. A member of our team will be in touch shortly.");
      setMessageType('success');
      reset();
      
    } catch (error) {
      console.error('❌ Form submission error:', error);
      
      // More specific error messages
      let errorMessage = "An error occurred while processing your request. Please try again.";
      
      if (error instanceof Error) {
        if (error.message.includes('fetch')) {
          errorMessage = "Unable to connect to server. Please check your internet connection.";
        } else if (error.message.includes('required')) {
          errorMessage = "Please fill in all required fields.";
        } else {
          errorMessage = error.message;
        }
      }
      
      setMessage(errorMessage);
      setMessageType('error');
      
    } finally {
      setLoading(false);
      setShowMessageModal(true);
      
      // Automatically hide the modal after 5 seconds
      setTimeout(() => {
        setShowMessageModal(false);
        setMessage("");
      }, 5000);
    }
  };

  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [description, setDescription] = useState("");
  
  const themes = getThemes || {}; // Add default if themes are undefined

  const closeModal = () => {
    const backdrop = document.querySelector('.hs-overlay-backdrop');
    if (backdrop) {
      // @ts-ignore
      backdrop.click();
    }
  };

  // White color detection helper
  const isWhiteOrShade = (color: string): boolean => {
    if (!color) return false;
    const s = color.trim().toLowerCase();
  
    // 1) explicit white checks
    if (
      s === "white" ||
      s === "#fff" ||
      s === "#ffffff" ||
      s === "rgb(255,255,255)" ||
      s === "rgba(255,255,255,1)"
    ) {
      return true;
    }
  
    let r: number, g: number, b: number;
  
    // 2) hex formats
    if (s.startsWith("#")) {
      let hex = s.slice(1);
      if (hex.length === 3) {
        // expand `#abc` → `#aabbcc`
        hex = hex.split("").map(ch => ch + ch).join("");
      }
      if (hex.length !== 6 || !/^[0-9a-f]{6}$/i.test(hex)) {
        return false;
      }
      r = parseInt(hex.slice(0, 2), 16);
      g = parseInt(hex.slice(2, 4), 16);
      b = parseInt(hex.slice(4, 6), 16);
    }
    // 3) rgb/rgba formats
    else {
      const m = s.match(
        /rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+)?\s*\)/
      );
      if (!m) return false;
      r = Number(m[1]);
      g = Number(m[2]);
      b = Number(m[3]);
    }
  
    // 4) threshold for "almost white"
    return r >= 235 && g >= 235 && b >= 235;
  };
  
  return (
    <>
      <div
        id="hs-large-modal"
        className="hs-overlay hidden w-full h-full fixed top-0 left-0 z-[70] overflow-x-hidden overflow-y-auto"
        onClick={(e: React.MouseEvent<HTMLDivElement>) => {
          if (e.target === e.currentTarget) {
            closeModal();
          }
        }}
      >
        <div className="hs-overlay-open:mt-20 hs-overlay-open:opacity-100 hs-overlay-open:duration-500 mt-0 opacity-0 ease-out transition-all lg:max-w-7xl lg:w-full m-3 lg:mx-auto rounded-b-xl relative" style={{background: "white"}}>
          <div className="relative w-full z-10" style={{ 
              backgroundImage: `url(${backgroundPattern})`, 
              backgroundRepeat: 'repeat', 
              backgroundSize: '75px 75px' 
            }}>
            <div className="flex flex-col shadow-sm rounded-xl relative z-20">
              <div className="flex justify-between items-center py-3 px-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    closeModal();
                  }}
                  className="absolute"
                  style={{
                    top: "50px",
                    right: "4px",
                    cursor: "pointer",
                    fontSize: "20px",
                    fontWeight: "bold",
                    zIndex: 1000,
                    width: "25px",
                    height: "25px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    transition: "transform 0.2s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.3)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  &times;
                </button>
              </div>
              <div className="overflow-y-auto pb-5">
                <div className="flex flex-wrap lg:flex-nowrap lg:space-x-4 font-poppins text-left p-10 font-light">
                  <div className="lg:w-1/2 w-full lg:pr-2 pr-0 p-4 border-gray-300 border shadow-sm rounded-xl lg:ml-[-20px]" style={{background: "rgba(255, 255, 255, 0.89)"}}>
                    <h5 className="text-xl">
                      Find Your Next Buy-To-Let Property
                    </h5>
                    <h3 className="text-6xl py-4">Get in touch</h3>
                    <h4 className="text-base tracking-wide">
                      If you would like to request information about any of the
                      properties available on our site, or would like to
                      understand how you can easily secure your next investment
                      property, please contact us via this form.
                    </h4>
                  </div>
                  <div className="lg:w-1/2 w-full lg:mt-0 mt-5">
                    <form
                      onSubmit={handleSubmit(onSubmit)}
                      className="flex flex-wrap"
                    >
                      <div className="w-1/2 pr-1 border-gray-200">
                        <input
                          value={firstname}
                          onInput={(e) => setFirstname(e.currentTarget.value)}
                          type="text"
                          required
                          className="py-3 mb-2 px-4 block w-full border-black border border-gray-200 rounded-md text-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-900 text-black"
                          placeholder="First Name*"
                          {...register("firstname", { required: true })}
                        />
                      </div>
                      <div className="w-1/2 pl-1">
                        <input
                          value={lastname}
                          onInput={(e) => setLastname(e.currentTarget.value)}
                          type="text"
                          required
                          className="py-3  mb-2 px-4 block w-full border-black border border-gray-200 rounded-md text-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-900 text-black"
                          placeholder="Last Name*"
                          {...register("lastname", { required: true })}
                        />
                      </div>
                      <div className="w-1/2 pr-1">
                        <input
                          onInput={(e) => setEmail(e.currentTarget.value)}
                          value={email}
                          type="email"
                          required
                          className="py-3 mb-2 px-4 block w-full border-black border border-gray-200 rounded-md text-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-900 text-black"
                          placeholder="Email*"
                          {...register("email", { required: true })}
                        />
                      </div>
                      <div className="w-1/2 pl-1">
                        <input
                          onInput={(e) => setPhone(e.currentTarget.value)}
                          value={phone}
                          type="tel"
                          required
                          className="py-3 mb-2 px-4 block w-full border-black border border-gray-200 rounded-md text-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-900 text-black"
                          placeholder="Phone*"
                          {...register("mobilephone", { required: true })}
                        />
                      </div>
                      <textarea
                        className="py-3 mb-3 px-4 block w-full border-black border border-gray-200 rounded-md text-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-900 text-black"
                        rows={3}
                        onInput={(e) => setDescription(e.currentTarget.value)}
                        value={description}
                        placeholder="Message"
                        required
                        {...register("message", { required: true })}
                      ></textarea>

                      <button
                        type="submit"
                        disabled={loading}
                        style={{
                          backgroundColor: themes.color2,
                          width: "30%",
                          color: isWhiteOrShade(themes.color1) ? "white" : themes.txtColor,
                          padding: "10px",
                          borderRadius: "5px",
                          border: "none",
                          cursor: loading ? "not-allowed" : "pointer",
                          transition: "background 0.3s ease, color 0.3s ease",
                          boxSizing: "border-box",
                          margin: "0 auto",
                          display: "block",
                          opacity: loading ? 0.7 : 1
                        }}
                        className="transition duration-300 ease-in-out"
                        onMouseOver={(e) => {
                          if (!loading) {
                            e.currentTarget.style.backgroundColor = isWhiteOrShade(themes?.color1) ? "black" : themes?.color1 || "alternateHoverColor";
                            e.currentTarget.style.color = isWhiteOrShade(themes?.color1) ? "white" : themes.txtColor;
                          }
                        }}
                        onMouseOut={(e) => {
                          if (!loading) {
                            e.currentTarget.style.backgroundColor = themes?.color2 || "defaultColor";
                            e.currentTarget.style.color = isWhiteOrShade(themes?.color1) ? "white" : themes.txtColor;
                          }
                        }}
                      >
                        {loading ? 'Sending...' : 'Send'}
                        {loading && (
                          <span
                            className="animate-spin border-btnscolor inline-block w-4 h-4 border-[3px] border-current border-t-transparent text-white rounded-full ml-2"
                            role="status"
                            aria-label="loading"
                          ></span>
                        )}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
            <img src={image1} alt="Cityscape" className="absolute rounded-b-xl pt-10 bottom-0 left-0 w-129 h-68 z-1"/>
          </div>
        </div>
      </div>

      {/* Message Modal */}
      {showMessageModal && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowMessageModal(false);
            }
          }}
        >
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full relative" style={{backgroundColor: "white"}}>
            <button
              onClick={() => setShowMessageModal(false)}
              className="absolute top-2 right-2 text-black-500 hover:text-black-700 text-xl font-bold w-6 h-6 flex items-center justify-center"
              aria-label="Close"
              style={{
                transition: "transform 0.2s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.3)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              &times;
            </button>
            <div className="flex flex-col items-center pt-4">
              <div className="mb-4">
                {messageType === 'success' ? (
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                ) : (
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </div>
                )}
              </div>
              <p className={`text-center text-lg ${messageType === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                {message}
              </p>
              <button
                onClick={() => setShowMessageModal(false)}
                className={`mt-4 px-6 py-2 rounded-md ${
                  messageType === 'success' 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : 'bg-red-600 hover:bg-red-700 text-white'
                } transition-colors duration-200`}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GenInTouch;